import React, {HTMLAttributes, useCallback} from 'react';
import {Comment} from "../../api/Models";
import useComments from "../../api/useComments";
import CreateCommentForm from "./CreateCommentForm";
import {ErrorBoundary, useEnvironment} from "@webng/react-app-common";
import CommentRow from '../CommentRow';
import {ExternalUserProvider} from "../../ExternalUserProvider";
import {ErrorPage} from "../ErrorPage";
import {useTranslation} from "react-i18next";
import useChannelSettings from "../../api/useChannelSettings";

import {RefreshCw, X} from "react-feather";
import TickarooLogo from '../shared/TickarooLogo';
import {TrackingFunction} from "../TrackingFunction";
interface CommentsModalProps {
  ownerId: string
  channelId: string
  externalUserProvider?: ExternalUserProvider,
  onClose: () => void
  onCommentManage?: (commentId: string) => void,
  onCommentCreated?: (comment: Comment) => void,
  version?: number
  useCookies?: boolean
  trackingFunction?: TrackingFunction
}

function ChannelContainer({children, ...attributes} : HTMLAttributes<HTMLDivElement>) {
  return <div className="tik4-comment-modal" {...attributes}>
    {children}
  </div>
}

function generateRandomWidthInRange(min:number, max:number, unit:string) {
  return `${Math.random() * (max - min) + min}${unit}`;
}

function inferLanguage(locale: string){
  if(locale === "de"){
    return "de";
  }
  return "en";
}

export function Channel({ownerId, channelId, externalUserProvider, onClose, onCommentCreated, onCommentManage, version, useCookies, trackingFunction}: CommentsModalProps){
  const {data: settings} = useChannelSettings({ownerId, channelId, version: (version || 0)})
  const {isLoading, isFetching, isFetchingNextPage, data, refetch, hasNextPage, fetchNextPage, total, newCount, error} = useComments({ownerId, channelId, limit: 50});
  const {t, i18n} = useTranslation("liveblog");
  const env = useEnvironment();

  const trackedFetchNextPage = useCallback(() => {
    trackingFunction && trackingFunction({t: "c_mor"})
    fetchNextPage()
  }, [fetchNextPage, trackingFunction])

  const handleCommentCreated = useCallback((comment: Comment)=> {
    trackingFunction && trackingFunction({t: "a_cwr"})

    if(onCommentCreated){
      onCommentCreated(comment);
    }
    if(comment.publishedAt){
      refetch();
    }
  }, [onCommentCreated, refetch, trackingFunction])

  if(isLoading){
    return <ChannelContainer>
      <div className="tik4-modal__header">
        <div className="tik4-modal__header-title">{t('liveblog.comments.modal.title')}</div>
        <button className="tik4-modal__close" onClick={onClose} type="button"><X className="tik4-icon tik4-modal__close__icon" /></button>
      </div>
      <div className="tik4-comment-modal__body">
        <div className="tik4-comment-skeleton tik4-skeleton">
          <div className="tik4-skeleton-pulse">
            {[...Array(12)].map((e, i) =>
              <div className="tik4-comment-skeleton___comment tik4-skeleton__round tik4-skeleton__bg--1" key={i}>
                <div className="tik4-skeleton__row">
                  <div className="tik4-skeleton__round tik4-skeleton__h-6 tik4-skeleton__bg--3" style={{width: generateRandomWidthInRange(15,40, '%')}}></div>
                  <div className="tik4-skeleton__grow">
                    <div className="tik4-skeleton__round tik4-skeleton__h-6 tik4-skeleton__bg--2" style={{width: generateRandomWidthInRange(70, 100, '%')}}></div>
                  </div>
                </div>
                <div className="tik4-skeleton__round tik4-skeleton__h-6 tik4-skeleton__bg--2" style={{width: generateRandomWidthInRange(50, 90, '%')}}></div>
                <div className="tik4-skeleton__round tik4-skeleton__h-4 tik4-skeleton__bg--2" style={{width: generateRandomWidthInRange(10, 40, '%')}}></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ChannelContainer>;
  }
  if(error){
    // TODO improve messages
    throw Error("Could not load comments " + error);
  }

  return <ChannelContainer>
    <div className="tik4-modal__header">
      <div className="tik4-modal__header-title">
        {total && total > 1 ? t('liveblog.comments.modal.title', {count: total}) :  t('liveblog.comments.modal.title') }
      </div>
      <button className="tik4-modal__close" onClick={onClose} type="button"><X className="tik4-icon tik4-modal__close__icon" /></button>
    </div>

    <ErrorBoundary FallbackComponent={ErrorPage}>
      {(settings && settings?.publishMode !== "ReadOnly") && <CreateCommentForm ownerId={ownerId} channelId={channelId} onSuccess={handleCommentCreated}
                          externalUserProvider={externalUserProvider} settings={settings} useCookies={useCookies}/>}
    </ErrorBoundary>

    <div className="tik4-comment-modal__body">

      <div className="tik4-comment-modal__body__container">
        { newCount > 0 &&
          <div className="tik4-new-comments">
            <button disabled={isFetching} className="tik4-button tik4-button--primary tik4-new-comments__button" onClick={refetch} type="button">
              <RefreshCw className={`tik4-icon tik4-icon--mr ${isFetching ? "tik4-spin" : ''}`}/>
              {t('liveblog.comments.modal.load_new_comments', {count: newCount})}
            </button>
          </div>
        }

        { data.length > 0 ? <>
            <div className="tik4-comments__list tik4-comments__list--modal">
              {data.map((item) =>
                <CommentRow comment={item} key={item.id} onCommentManage={onCommentManage} />
              )}
            </div>
            { hasNextPage && <div className="tik4-more-comments">
                <button className="tik4-more-comments__button" onClick={trackedFetchNextPage} disabled={isFetchingNextPage} type="button">{t('liveblog.comments.modal.show_more_comments')}</button>
              </div>
            }
          </>:
            <div className="tik4-no-comments">
              {t('liveblog.comments.modal.be_first',)}
            </div>
        }
      </div>

      <a className="tik4-comments__powered-by" href={`https://tickaroo.com/${inferLanguage(i18n.language)}/liveblog-software?utm_source=${env.clientId || "editor"}&utm_medium=embedjs-comments&utm_term=liveblog-by-tickaroo&utm_campaign=Product%20Link`}>

        <TickarooLogo width={55} height={12} /> Live Blog Software<br />

      </a>
    </div>



  </ChannelContainer>;
}
